import React, { useState } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import styles from "../Admin.module.scss";
import Button from "../../../components/Button/Button";
import Checkbox from "../../../components/Checkbox/Checkbox";
import dragIcon from "../../../resources/images/dragIndicator.svg";

export interface ColumnOption {
  id: string;
  label: string;
}

interface Props {
  // All columns the table knows about, in their original declaration order.
  allColumns: ColumnOption[];
  // Currently-visible IDs in the admin's chosen order.
  visibleIds: string[];
  saveCallback: (orderedVisibleIds: string[]) => void;
}

interface Row {
  id: string;
  label: string;
  visible: boolean;
}

const buildInitialRows = (
  allColumns: ColumnOption[],
  visibleIds: string[]
): Row[] => {
  // First: the visible columns in the admin's saved order.
  const seen = new Set<string>();
  const rows: Row[] = [];
  for (const id of visibleIds) {
    const col = allColumns.find((c) => c.id === id);
    if (!col || seen.has(id)) continue;
    rows.push({ id: col.id, label: col.label, visible: true });
    seen.add(id);
  }
  // Then: any remaining columns that aren't currently visible, in their
  // original declaration order (so newly-added columns surface naturally).
  for (const col of allColumns) {
    if (seen.has(col.id)) continue;
    rows.push({ id: col.id, label: col.label, visible: false });
  }
  return rows;
};

const ColumnsModal: React.FC<Props> = ({
  allColumns,
  visibleIds,
  saveCallback,
}) => {
  const [rows, setRows] = useState<Row[]>(() =>
    buildInitialRows(allColumns, visibleIds)
  );

  const toggle = (id: string) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, visible: !r.visible } : r))
    );
  };

  const showAll = () =>
    setRows((prev) => prev.map((r) => ({ ...r, visible: true })));

  const resetDefault = () =>
    setRows(
      allColumns.map((c) => ({ id: c.id, label: c.label, visible: true }))
    );

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const src = result.source.index;
    const dst = result.destination.index;
    if (src === dst) return;
    setRows((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(src, 1);
      next.splice(dst, 0, moved);
      return next;
    });
  };

  const submit = () => {
    const ordered = rows.filter((r) => r.visible).map((r) => r.id);
    saveCallback(ordered);
  };

  return (
    <div>
      <div className={styles.columnsModal__Body}>
        <div className={styles.columnsModal__Hint}>
          Drag rows to reorder. Check or uncheck to show or hide a column.
        </div>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="columns">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={styles.columnsModal__List}
              >
                {rows.map((row, index) => (
                  <Draggable key={row.id} draggableId={row.id} index={index}>
                    {(dragProvided) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        className={styles.columnsModal__Row}
                      >
                        <img
                          src={dragIcon}
                          alt="Drag to reorder"
                          className={styles.columnsModal__DragHandle}
                          {...dragProvided.dragHandleProps}
                        />
                        <Checkbox
                          checked={row.visible}
                          onChange={() => toggle(row.id)}
                        >
                          {row.label}
                        </Checkbox>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      <div className={styles.filterLicenseModal__Footer}>
        <div className={styles.filterLicenseModal__ButtonsWrapper}>
          <Button theme={"secondary"} size={"small"} onClick={showAll}>
            Show All
          </Button>
          <Button theme={"secondary"} size={"small"} onClick={resetDefault}>
            Reset to Default
          </Button>
          <Button size={"small"} onClick={submit}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ColumnsModal;
